// React Hooks:
import React, { useState, useEffect, useRef, createRef } from "react";
// Redux Toolkit Hooks:
import { useAppSelector, useAppDispatch } from "../../../../../app/hooks";
// Redux Toolkit Actions & State:
import {
  Items,
  directory,
  getDirectoryAction,
  searchInputAction,
  resetSearchDirectoryAction,
} from "../directorySlice";
import {
  directoryItemsTouchedAction,
  directoryItemsGetInfoAction,
  directoryItemsRemoveInfoAction,
  directoryItems,
  ItemInfo,
} from "./directoryItemsSlice";
import { drawerOpenOrCloseAction } from "../../drawer/drawerSlice";
import {
  drawerInfo,
  resetRenameAction,
} from "../../drawer/drawer-item/drawer-info/drawerInfoSlice";
import { getShareManagementListAction } from "../../../share-management/shareManagementSlice";
// MUI Components:
import { Box, Typography, SvgIcon, Tooltip, useTheme } from "@mui/material";
// Components:
import DirectoryItemsOption from "./directory-items-option/DirectoryItemsOption";
// MUI Styles:
import { makeStyles } from "@mui/styles";
// Icones:
import { ReactComponent as DotsIcon } from "../../../../../assets/icons/3dots-icon.svg";

const useStyles = makeStyles((theme) => ({
  itemsBox: {
    position: "relative",
    borderRadius: "8px",
    backgroundColor: "transparent",
    color: "transparent",
    "&:hover": {
      transition: "all 0.4s ease",
      boxShadow: theme.shadows[3],
      cursor: "pointer",
      color: theme.palette.text.secondary,
    },
  },
  activeItemBox: {
    boxShadow: theme.shadows[3],
    cursor: "pointer",
    color: theme.palette.text.secondary,
  },
  images: {
    color: theme.palette.secondary.light,
    objectFit: "cover",
  },
}));

const isMobile = () => {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  ) {
    return true;
  } else {
    return false;
  }
};

const byteToMegabyte = (e: number) => {
  const result = e / (1024 * 1024);
  return result.toFixed(2);
};

const DirectoryItems = () => {
  const classes = useStyles();
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const { listView, directoryData, searchData, searchInput } =
    useAppSelector(directory);
  const { itemInfo } = useAppSelector(directoryItems);
  const directoryDeleteModal = useAppSelector(
    (state) => state.deleteModalState
  );
  const alternativeFileModal = useAppSelector(
    (state) => state.alternativeFileModalState
  );
  const { renameData } = useAppSelector(drawerInfo);

  const [items, setItems] = useState<Array<Items>>(directoryData.items);

  const ItemBoxRef: any = useRef([]);
  ItemBoxRef.current = items?.map(
    (element, i) => ItemBoxRef.current[i] ?? createRef()
  );
  
  const [activeItemBox, setActiveItemBox] = useState<any>({
    ref: null,
    id: -1,
  });

  const [popoverOptions, setPopoverOptions] = useState({
    anchorEl: null,
    popId: -1,
  });

  useEffect(() => {
    if (searchInput === "") {
      dispatch(resetSearchDirectoryAction());
      setItems(directoryData.items);
    } else if (searchData.actionStatus === "success") {
      setItems(searchData.items);
      dispatch(resetSearchDirectoryAction());
    }
  }, [
    searchInput,
    searchData,
    directoryData.items,
    searchData.items,
    dispatch,
  ]);

  useEffect(() => {
    const disableTouch = (e: any) => {
      if (
        e.target !== activeItemBox.ref &&
        !e
          .composedPath()
          .map((item: any) => item.id)
          .find((item: any) => item === "ignoreDisableTouch")
      ) {
        setActiveItemBox({ ref: null, id: -1 });
        dispatch(directoryItemsRemoveInfoAction());
        dispatch(directoryItemsTouchedAction(false));
        dispatch(drawerOpenOrCloseAction(false));
        dispatch(resetRenameAction());
      }
    };

    if (
      activeItemBox.ref !== null &&
      directoryDeleteModal.openModal === false &&
      alternativeFileModal.openModal === false
    ) {
      document.body.addEventListener("click", disableTouch, { capture: true });
    }

    return () => {
      document.body.removeEventListener("click", disableTouch, {
        capture: true,
      });
    };
  }, [
    activeItemBox.ref,
    dispatch,
    directoryDeleteModal.openModal,
    alternativeFileModal.openModal,
  ]);

  useEffect(() => {
    if (!!renameData.newName) {
      const selectedItem = sortItems(items).find(
        (item) =>
          item.name === renameData.newName &&
          (item.dir
            ? item.path
                .split("/")
                .filter(
                  (item2: string, index2: number) =>
                    index2 > 0 && index2 < item.path.split("/").length - 1
                )
                .join("/")
            : item.path
                ?.split("/")
                .filter((item: string, index: number) => index > 0)
                .join("/")) === itemInfo.path
      );
      const selectedIndex = sortItems(items).findIndex(
        (item) =>
          item.name === renameData.newName &&
          (item.dir
            ? item.path
                .split("/")
                .filter(
                  (item2: string, index2: number) =>
                    index2 > 0 && index2 < item.path.split("/").length - 1
                )
                .join("/")
            : item.path
                ?.split("/")
                .filter((item: string, index: number) => index > 0)
                .join("/")) === itemInfo.path
      );

      if (!!selectedItem) {
        setActiveItemBox({
          ref: ItemBoxRef.current[selectedIndex]?.current,
          id: selectedIndex,
        });
        dispatch(directoryItemsGetInfoAction(selectedItem));
        dispatch(getShareManagementListAction({ file_id: selectedItem.id }));
      }
    } else {
      const selectedIndex = sortItems(items).findIndex(
        (item) =>
          item.name === itemInfo.name &&
          (item.dir
            ? item.path
                .split("/")
                .filter(
                  (item2: string, index2: number) =>
                    index2 > 0 && index2 < item.path.split("/").length - 1
                )
                .join("/")
            : item.path
                ?.split("/")
                .filter((item: string, index: number) => index > 0)
                .join("/")) === itemInfo.path
      );

      if (!!itemInfo.name) {
        setActiveItemBox({
          ref: ItemBoxRef.current[selectedIndex]?.current,
          id: selectedIndex,
        });
      }
    }
  }, [dispatch, itemInfo.name, itemInfo.path, items, renameData.newName]);

  const handleOptionClick = (event: any, id: number) => {
    setPopoverOptions({
      anchorEl: event.currentTarget.children[0].children[0],
      popId: id,
    });
  };
  const handleClosePopover = (item: ItemInfo, index: number) => {
    setActiveItemBox({
      ref: ItemBoxRef.current[index]?.current,
      id: index,
    });
    dispatch(directoryItemsGetInfoAction(item));
    setPopoverOptions({ anchorEl: null, popId: -1 });
  };

  const openPopover = Boolean(popoverOptions.anchorEl);
  const id = openPopover ? "ignoreDisableTouch" : undefined;

  const sortItems = (items: any) => {
    const sortByNames = [...items].sort((a: any, b: any) => {
      const x = a.name?.toLowerCase();
      const y = b.name?.toLowerCase();

      return x < y ? -1 : x > y ? 1 : 0;
    });

    const folders = sortByNames.filter((item: any) => item.dir === true);
    const files = sortByNames.filter((item: any) => item.dir === false);
    return [...folders, ...files];
  };

  const setImageSrc = (item: { dir: boolean; type: string }) => {
    const checkImageExists = (name: string) => {
      try {
        require(`../../../../../assets/megaDL-icones/${name}.png`);
        return true;
      } catch (error) {
        return false;
      }
    };

    if (item.dir === true) {
      return require(`../../../../../assets/megaDL-icones/folder.png`);
    } else if (
      item.dir === false &&
      !!item.type &&
      checkImageExists(item.type)
    ) {
      return require(`../../../../../assets/megaDL-icones/${item.type}.png`);
    } else {
      return require(`../../../../../assets/megaDL-icones/none.png`);
    }
  };

  return (
    <>
      {sortItems(items)
        // .filter((item) =>
        //   item.name
        //     ?.toLocaleLowerCase()
        //     .includes(searchInput?.toLocaleLowerCase())
        // )
        .map((item, index) => (
          <Box
            key={index}
            ref={ItemBoxRef.current[index]}
            sx={{
              width: `${listView ? "100%" : "120px"}`,
              height: `${listView ? "75px" : "130px"}`,
              minHeight: `${listView ? "75px" : "fit-content"}`,
              padding: "16px",
              margin: `${listView ? "4px 0px 4px 0px" : "4px 4px 4px 4px"}`,
              display: "flex",
              flexDirection: `${listView ? "row" : "column"}`,
              alignItems: "center",
              justifyContent: `${listView ? "flex-start" : "space-between"}`,
              transition: "height 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28)",
              "&:first-of-type": {
                marginTop: `${listView ? "8px" : ""}`,
              },
              [theme.breakpoints.down("md")]: {
                width: `${listView ? "100%" : "95px"}`,
                height: `${listView ? "55px" : "105px"}`,
                minHeight: `${listView ? "55px" : "fit-content"}`,
              },
            }}
            className={`${classes.itemsBox} ${
              activeItemBox.id === index && activeItemBox.ref !== null
                ? classes.activeItemBox
                : ""
            }`}
            onClick={(event: any) => {
              event.stopPropagation();
              dispatch(resetRenameAction());
              if (isMobile() === false) {
                if (popoverOptions.anchorEl === null) {
                  setActiveItemBox({
                    ref: ItemBoxRef.current[index]?.current,
                    id: index,
                  });
                  dispatch(directoryItemsGetInfoAction(item));
                  dispatch(getShareManagementListAction({ file_id: item.id }));
                }
              } else {
                event.stopPropagation();
                if (item.dir) {
                  dispatch(searchInputAction(""));
                  dispatch(directoryItemsRemoveInfoAction());
                  dispatch(directoryItemsTouchedAction(false));
                  dispatch(drawerOpenOrCloseAction(false));
                  dispatch(
                    getDirectoryAction({
                      path: item.path,
                      idfolder: item.id,
                    })
                  );
                }
              }
            }}
            onDoubleClick={(event) => {
              event.stopPropagation();
              dispatch(resetRenameAction());
              if (isMobile() === false) {
                dispatch(directoryItemsRemoveInfoAction());
                dispatch(directoryItemsTouchedAction(false));
                dispatch(drawerOpenOrCloseAction(false));
                if (item.dir) {
                  dispatch(searchInputAction(""));
                  dispatch(
                    getDirectoryAction({
                      path: item.path,
                      idfolder: item.id,
                    })
                  );
                }
              }
            }}
            onTouchEnd={(event) => {
              event.stopPropagation();
              if (isMobile() === true) {
                setActiveItemBox({
                  ref: ItemBoxRef.current[index]?.current,
                  id: index,
                });
                dispatch(directoryItemsGetInfoAction(item));
                dispatch(getShareManagementListAction({ file_id: item.id }));
              }
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              handleOptionClick(event, index);
              setActiveItemBox({
                ref: ItemBoxRef.current[index]?.current,
                id: index,
              });
              dispatch(directoryItemsGetInfoAction(item));
              dispatch(getShareManagementListAction({ file_id: item.id }));
            }}
            id="ignoreDisableTouch"
          >
            <Box
              sx={{
                width: "20px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                top: "0px",
                right: "0px",
                borderTopRightRadius: "8px",
                color: "inherit",
              }}
              onClick={(event) => {
                event.stopPropagation();
                handleOptionClick(event, index);
                setActiveItemBox({
                  ref: ItemBoxRef.current[index]?.current,
                  id: index,
                });
                dispatch(directoryItemsGetInfoAction(item));
                dispatch(getShareManagementListAction({ file_id: item.id }));
              }}
              onDoubleClick={(event) => event.stopPropagation()}
            >
              <SvgIcon
                component={DotsIcon}
                inheritViewBox
                sx={{
                  width: "auto",
                  height: "14px",
                  color: "inherit",
                }}
              />
            </Box>

            <Box
              sx={{
                width: `${listView ? "55px" : "85px"}`,
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.4s",
                [theme.breakpoints.down("md")]: {
                  width: `${listView ? "40px" : "60px"}`,
                },
              }}
              className={classes.images}
            >
              <Box
                component="img"
                src={setImageSrc(item)}
                alt={item.name}
                sx={{
                  width: "auto",
                  height: `${
                    item.dir && listView ? "40px" : listView ? "50px" : "60px"
                  }`,
                  [theme.breakpoints.down("md")]: {
                    height: `${
                      item.dir && listView ? "30px" : listView ? "35px" : "45px"
                    }`,
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                width: `${listView ? "calc(100% - 55px)" : "100%"}`,
                height: `${listView ? "100%" : "fit-content"}`,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                [theme.breakpoints.down("md")]: {
                  width: `${listView ? "calc(100% - 40px)" : "100%"}`,
                },
              }}
            >
              <Box
                sx={{
                  width: `${listView ? "35%" : "100%"}`,
                  paddingLeft: `${listView ? "8px" : ""}`,
                }}
              >
                <Tooltip
                  arrow
                  placement="bottom"
                  title={item.name}
                  enterDelay={2000}
                  enterNextDelay={2000}
                  leaveDelay={500}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        marginTop: "6px !important",
                      },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      width: `${listView ? "fit-content" : "100%"}`,
                      maxWidth: "100%",
                      height: "24px",
                      textAlign: `${listView ? "left" : "center"}`,
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      fontSize: "16px",
                      color: (theme) => theme.palette.text.secondary,
                      [theme.breakpoints.down("md")]: {
                        fontSize: "12px",
                        height: "16px",
                      },
                    }}
                  >
                    {item.name}
                  </Typography>
                </Tooltip>
              </Box>

              {listView && (
                <Box
                  sx={{
                    width: `${listView ? "30%" : "100%"}`,
                    paddingLeft: `${listView ? "8px" : ""}`,
                  }}
                >
                  <Typography
                    sx={{
                      width: `${listView ? "fit-content" : "100%"}`,
                      maxWidth: "100%",
                      height: "24px",
                      textAlign: `${listView ? "left" : "center"}`,
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      fontSize: "16px",
                      color: (theme) => theme.palette.text.secondary,
                      [theme.breakpoints.down("md")]: {
                        fontSize: "12px",
                        height: "16px",
                      },
                    }}
                  >
                    {item.modify}
                  </Typography>
                </Box>
              )}

              {listView && (
                <Box
                  sx={{
                    width: `${listView ? "20%" : "100%"}`,
                    paddingLeft: `${listView ? "8px" : ""}`,
                  }}
                >
                  <Typography
                    sx={{
                      width: `${listView ? "fit-content" : "100%"}`,
                      maxWidth: "100%",
                      height: "24px",
                      textAlign: `${listView ? "left" : "center"}`,
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      fontSize: "16px",
                      paddingLeft: `${listView ? "8px" : ""}`,
                      color: (theme) => theme.palette.text.secondary,
                      [theme.breakpoints.down("md")]: {
                        fontSize: "12px",
                        height: "16px",
                      },
                    }}
                  >
                    {item.dir && "File folder"}
                    {item.type}
                  </Typography>
                </Box>
              )}

              {listView && (
                <Box
                  sx={{
                    width: `${listView ? "15%" : "100%"}`,
                    paddingLeft: `${listView ? "8px" : ""}`,
                  }}
                >
                  <Typography
                    sx={{
                      width: `${listView ? "fit-content" : "100%"}`,
                      maxWidth: "100%",
                      height: "24px",
                      textAlign: `${listView ? "left" : "center"}`,
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      fontSize: "16px",
                      color: (theme) => theme.palette.text.secondary,
                      [theme.breakpoints.down("md")]: {
                        fontSize: "12px",
                        height: "16px",
                      },
                    }}
                  >
                    {!item.dir &&
                      +item.size &&
                      `${byteToMegabyte(+item.size)} mb`}
                  </Typography>
                </Box>
              )}
            </Box>

            <DirectoryItemsOption
              item={item}
              index={index}
              id={id}
              openPopover={openPopover}
              popoverOptions={popoverOptions}
              handleClosePopover={handleClosePopover}
            />
          </Box>
        ))}
    </>
  );
};

export default DirectoryItems;
